import React, { useState } from 'react'
import { useNavigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { styled, Stack, Tab, Tabs, Skeleton, Typography } from '@mui/material'

import { useUser } from 'store'
import TabPanel from 'components/TabPanel'
import { SideContent as BaseSideContent } from 'components-shared'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { toast } from 'components/global/CUK/Toast/Toast'
import CardPreview, { CardBlockInfo, CardHeaderTitle, CardBody } from 'packages/ui/CardPreview'

import { Status } from '../Table/columns-config'
import { useDetail } from '../use-bypass'
import { useDeactivate } from '../use-actions'
import { DeactivatedAt } from '../Table/columns-config'
import History from './History'

const SideContent = styled(BaseSideContent)(() => ({
  width: 'clamp(420px, 50%, 500px)',
  overflow: 'hidden'
}))

const ActionActivate = () => {
  const navigate = useNavigate()
  const {t} = useTranslation()

  return (
    <CardActionButton
      /* @ts-expect-error JSX */
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        width: '32px',
        height: '32px',
        alignSelf: 'center',
      }}
      iconlabel={'play'}
      tooltiptitle={t('bypass.actions.activate_toolip')}
      onClick={() => navigate('./activate')}
    />
  )
}

const ActionDeactivate:React.FC<{onError: (v: string|null) => void}> = ({onError = () => {}}) => {
  const {id} = useParams()
  const [deactivate, {loading}] = useDeactivate()
  const {t} = useTranslation()

  const handleClick = async () => {
    onError(null)
    const {error} = await deactivate(id as string)
    if (error) {
      toast.error(t('bypass.deactivate.error'))
      onError(error as string)
    } else {
      toast.success(t('bypass.deactivate.success'))
    }
  }

  return (
    <CardActionButton
      /* @ts-expect-error JSX */
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        width: '32px',
        height: '32px',
        alignSelf: 'center',
      }}
      iconlabel={loading ? 'spinner fa-spin' : 'pause'}
      tooltiptitle={t('bypass.actions.deactivate_toolip')}
      onClick={handleClick}
    />
  )
}

const Card: React.FC = () => {
  const {t} = useTranslation()
  const [errorAction, setErrorAction] = useState<string|null>(null)
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState('infos')
  const [user] = useUser()
  const {loading, error, item} = useDetail()

  return (
    <>
      <SideContent>
        <CardPreview
          title={t('ui_elements.card_title')}
          loading={loading}
          error={error ? t('shared.error') : null}
          onClose={() => navigate({ pathname: '..' })}
          components={{
            ActionButtons: () => {
              return (
                <Stack direction={'row'}>
                  <CardActionButton
                    iconlabel={'pen'}
                    tooltiptitle={t('bypass.actions.edit_toolip')}
                    onClick={() => navigate('./edit')}
                  />
                  <CardActionButton
                    iconlabel={'trash'}
                    tooltiptitle={t('bypass.actions.trash_toolip')}
                    onClick={() => navigate('./remove')}
                  />
                </Stack>
              )
            }
          }}
        >
          {
            loading
              ? (
                <Stack gap={4}>
                  <Stack alignSelf={'center'}>
                    <Skeleton variant={'rectangular'} width={150} height={40} />
                  </Stack>
                  <Skeleton variant={'rectangular'} width={'100%'} height={200} />
                </Stack>
              )
              : (
                <Stack gap={1}>
                  <CardHeaderTitle>
                    <Stack gap={1} justifyContent={'center'}>
                      <Status item={item} />
                      {item?.mac_address}
                      {
                        item?.active
                          ? <ActionDeactivate onError={setErrorAction} />
                          : <ActionActivate />
                      }
                      {
                        Boolean(errorAction) && (
                          <Typography sx={{fontSize : 12,fontWeight: 500, color: 'error.main' }}>
                            {t(`api_errors.${errorAction}`)}
                          </Typography>
                        )
                      }
                    </Stack>
                  </CardHeaderTitle>
                  <Tabs
                    value={selectedTab}
                    onChange={(e, value) => {
                      setSelectedTab(value)
                    }}
                    sx={{
                      padding: 0,
                      fontWeight: 300
                    }}>
                    <Tab component="div" label={t('bypass.tabs.infos')} value="infos" />
                    {
                      user?.admin && (
                        <Tab component="div" label={t('bypass.tabs.history')} value="history" />
                      )
                    }
                  </Tabs>
                  {
                    selectedTab === 'infos' && (
                      <TabPanel value={'infos'} index="infos">
                        <CardBody grid>
                          <CardBlockInfo
                            title={t('bypass.validity_date')}
                          >
                            <DeactivatedAt item={item} />
                          </CardBlockInfo>
                          <CardBlockInfo
                            title={t('bypass.table.created_at_col')}
                            subtitle={new Date(item?.created_at).toLocaleDateString()}
                          />
                          <CardBlockInfo
                            title={t('bypass.table.created_by_col')}
                            subtitle={item?.created_by?.email}
                          />
                          <CardBlockInfo
                            title={t('bypass.table.updated_by_col')}
                            subtitle={item?.updated_by?.email}
                          />
                          <CardBlockInfo
                            title={t('bypass.description')}
                            subtitle={item?.description}
                          />
                        </CardBody>
                      </TabPanel>
                    )
                  }
                  {
                    selectedTab === 'history' && (
                      <TabPanel value={'history'} index="history">
                        <History item={item} />
                      </TabPanel>
                    )
                  }
                </Stack>
              )
          }
        </CardPreview>
      </SideContent>
      <Outlet />
    </>
  )
}

export default Card
